import { useEffect, useRef, useState } from 'react'
import { usePreviousDistinct } from 'react-use'
import {
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from '@material-ui/core'
import {
  CallMadeRounded,
  ClearRounded,
  ReportProblemRounded,
} from '@material-ui/icons'
import { Pagination } from '@material-ui/lab'
import { SearchRounded } from '@material-ui/icons'
import PlayerList from './PlayerList'
import { filterPlayers } from '../utils/player'
import type { Player } from '../types'

const PAGE_SIZE = 10

type Group = 'all' | 'popular'
interface Props {
  group: Group
  onSelect: (playerId: string) => void
  onSwitchGroup: (group: Group) => void
  players: Player[]
}

const PlayerResults = ({
  group,
  onSelect,
  onSwitchGroup,
  players: allPlayers,
}: Props) => {
  const { palette } = useTheme()
  const rootRef = useRef<HTMLDivElement>(null)
  const [page, setPage] = useState(1)
  const previousPage = usePreviousDistinct(page)
  const [filter, setFilter] = useState('')

  // TODO: Figure out how to debounce so we're not
  // recalculating `filteredPlayers` while the user is still typing

  const filteredPlayers = filterPlayers(allPlayers, filter)

  const numPages = Math.ceil(filteredPlayers.length / PAGE_SIZE)
  const startIndex = (page - 1) * PAGE_SIZE
  const pageOfPlayers = filteredPlayers.slice(
    startIndex,
    startIndex + PAGE_SIZE,
  )

  useEffect(() => {
    // avoid scrolling when the list first loads
    if (rootRef.current && previousPage) {
      rootRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [previousPage])

  return (
    <div ref={rootRef}>
      {allPlayers.length > 0 && (
        <Box my={2}>
          <TextField
            id="player-results-player-filter"
            label="Find a player"
            type="search"
            value={filter}
            color="secondary"
            onChange={(e) => {
              setPage(1)
              setFilter(e.target.value)
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchRounded color="secondary" />
                </InputAdornment>
              ),
            }}
            style={{ width: '100%' }}
          />
        </Box>
      )}
      {filteredPlayers.length > 0 ? (
        <>
          <PlayerList
            players={pageOfPlayers}
            onSelect={onSelect}
            includeDividers
          />

          <Box mt={2} display="flex" justifyContent="center">
            <Pagination
              count={numPages}
              page={page}
              siblingCount={0}
              onChange={(_, newPage) => setPage(newPage)}
            />
          </Box>
        </>
      ) : (
        <Box mt={4} display="flex" flexDirection="column" alignItems="center">
          <ReportProblemRounded
            style={{
              display: 'block',
              fontSize: 125,
              color: palette.warning.main,
            }}
          />
          <Box mt={2}>
            <Typography variant="h5" align="center">
              No players found.
            </Typography>
          </Box>
          <Box mt={2}>
            {filter && group === 'all' && (
              <Button
                variant="outlined"
                startIcon={<ClearRounded />}
                onClick={() => setFilter('')}
              >
                Clear search
              </Button>
            )}
            {group !== 'all' && (
              <Button
                variant="outlined"
                endIcon={<CallMadeRounded />}
                onClick={() => onSwitchGroup('all')}
              >
                View all players
              </Button>
            )}
          </Box>
        </Box>
      )}
    </div>
  )
}

export default PlayerResults
